<template>
    <div>
        <Panel id="assign_numbers" :title="$t('Assign Access Numbers')" :help-text="$t('DESC_ASSIGN_NUMBERS')">
            <div id="assign_numbers_body">
                <ul>
                    <li class="search-section">
                        <b>{{ $t('Selected organisation') }}:</b>
                        <div class="selected-name">{{ selected.name }}</div>
                        <CicTextbox
                            id="prefix_search"
                            :value="searchText"
                            @keyup.enter="handleSearch"
                            @input="(val) => (searchText = val)"
                        />
                    </li>
                    <li>
                        <SearchTree
                            v-if="lastSearch !== '' && organisations"
                            :data="organisations"
                            :selected="selected.id"
                            @click="handleClick"
                        />
                        <LazyTree
                            v-else
                            :org-id="$store.user.orgId"
                            :org-name="$store.user.orgName"
                            @click="handleClick"
                        />
                    </li>
                </ul>
                <div class="sites-section">
                    <ul>
                        <li v-if="selectedSites.length > 0" class="inline-form-item">
                            <div>{{ $t('Site') }}</div>
                            <div>
                                <CicSelect
                                    id="assign_numbers_site"
                                    :options="selectedSites.map((site) => ({ value: site.siteId, label: site.name }))"
                                    :placeholder="$t('None')"
                                    :value="siteId"
                                    clearable
                                    @input="(val) => (siteId = val)"
                                />
                            </div>
                        </li>
                        <li v-if="selectedGroups.length > 0" class="inline-form-item">
                            <div>{{ $t('User Group') }}</div>
                            <div>
                                <CicSelect
                                    id="assign_numbers_group"
                                    :options="
                                        selectedGroups.map((group) => ({ value: group.userGroupId, label: group.name }))
                                    "
                                    :placeholder="$t('None')"
                                    :value="groupId"
                                    clearable
                                    @input="(val) => (groupId = val)"
                                />
                            </div>
                        </li>
                    </ul>
                    <div
                        v-if="
                            $store.hasRight('ASSIGN_TO_SUBORGANISATIONS') ||
                            $store.hasRight('RETRIEVE_FROM_SUBORGANISATIONS')
                        "
                    >
                        <div class="form-label-first">{{ $t('Assign range') }}</div>
                        <ul>
                            <li class="inline-form-item">
                                <div>{{ $t('From') }}</div>
                                <div>
                                    <CicTextbox
                                        id="assign_range_from"
                                        :value="rangeFrom"
                                        @input="(val) => (rangeFrom = val)"
                                    />
                                </div>
                            </li>
                            <li class="inline-form-item">
                                <div>{{ $t('To') }}</div>
                                <div>
                                    <CicTextbox
                                        id="assign_range_to"
                                        :value="rangeTo"
                                        @input="(val) => (rangeTo = val)"
                                    />
                                </div>
                            </li>
                            <li id="assign_numbers_controls">
                                <CicButton id="assign_range" :enabled="rangeAssignable" @click="assignRange">
                                    {{ $t('Assign range') }}
                                </CicButton>
                                <CicButton id="retrieve_range" :enabled="rangeAssignable" @click="retrieveRange">
                                    {{ $t('Retrieve range') }}
                                </CicButton>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="numbers-section">
                    <div class="paging-section">
                        <div>
                            <span>{{ $t('Total number of records') }}: </span>
                            <span>{{ totalRows }}</span>
                        </div>

                        <CicPageControl
                            :page="page"
                            :total-rows="totalRows"
                            :results-per-page="12"
                            @change="changePage"
                        />
                    </div>
                    <div>
                        <table id="assign_numbers_table" class="cic-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('Access Point') }}</th>
                                    <th>{{ $t('Organisation') }}</th>
                                    <th>{{ $t('Site') }}</th>
                                    <th>{{ $t('User Group') }}</th>
                                    <th>{{ $t('Description') }}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="number in numbers">
                                    <td>{{ number.name }}</td>
                                    <td>{{ tableOrganisations.find((org) => org.orgId === number.orgId).name }}</td>
                                    <td>
                                        {{
                                            number.siteId > 0 && sites.some((site) => site.siteId === number.siteId)
                                                ? sites.find((site) => site.siteId === number.siteId).name
                                                : $t('None')
                                        }}
                                    </td>
                                    <td>
                                        {{
                                            number.userGroupId > 0 &&
                                            groups.some((group) => group.userGroupId === number.userGroupId)
                                                ? groups.find((group) => group.userGroupId === number.userGroupId).name
                                                : $t('None')
                                        }}
                                    </td>
                                    <td>{{ number.description }}</td>
                                    <td class="multi-line-control">
                                        <span
                                            v-if="
                                                number.orgId === $store.user.orgId &&
                                                $store.hasRight('ASSIGN_TO_SUBORGANISATIONS')
                                            "
                                            class="assign-button"
                                            :title="$t('Assign number')"
                                            @click="assignNumber(number.accessId)"
                                        ></span>
                                        <span
                                            v-if="
                                                number.orgId !== $store.user.orgId &&
                                                $store.hasRight('RETRIEVE_FROM_SUBORGANISATIONS')
                                            "
                                            class="remove-button"
                                            :title="$t('Retrieve number')"
                                            @click="retrieveNumber(number.accessId)"
                                        >
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Panel>

        <div v-if="$store.showLoadingOverlay" class="cic-overlay"></div>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicPageControl from '@/components/CicPageControl';
    import Panel from '@/components/container/Panel';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import LazyTree from '@/feature/suborganisationLogin/LazyTree';
    import SearchTree from '@/feature/suborganisationLogin/SearchTree';
    import { load, save } from '@/utils/Http';
    import './styles/assignNumbers.css';

    export default {
        name: 'AssignNumbers',
        components: {
            CicButton,
            CicPageControl,
            CicSelect,
            CicTextbox,
            LazyTree,
            Panel,
            SearchTree,
        },
        data() {
            return {
                groupId: null,
                groups: [],
                lastSearch: '',
                numbers: [],
                organisations: null,
                page: 1,
                rangeFrom: null,
                rangeTo: null,
                searchText: '',
                selected: null,
                siteId: null,
                sites: [],
                tableOrganisations: [],
                totalRows: 0,
            };
        },
        computed: {
            rangeAssignable() {
                return (
                    this.rangeFrom &&
                    this.rangeFrom.trim() !== '' &&
                    !isNaN(this.rangeFrom) &&
                    this.rangeTo &&
                    this.rangeTo.trim() !== '' &&
                    !isNaN(this.rangeTo) &&
                    Number(this.rangeTo) >= Number(this.rangeFrom)
                );
            },
            selectedGroups() {
                return this.groups.filter((group) => group.orgId === this.selected.id);
            },
            selectedSites() {
                return this.sites.filter((site) => site.orgId === this.selected.id);
            },
        },
        created() {
            this.tableOrganisations = [{ orgId: this.$store.user.orgId, name: this.$store.user.orgName }];
            this.selected = { id: this.$store.user.orgId, name: this.$store.user.orgName };
            this.loadNumbers();
        },
        methods: {
            assignNumber(accessId) {
                if (
                    confirm(
                        this.$t(this.selected.id === this.$store.user.orgId ? 'ASSIGN_CONFIRM_1' : 'ASSIGN_CONFIRM_2'),
                    )
                ) {
                    const params = {
                        orgId: this.selected.id,
                        siteId: this.siteId,
                        userGroupId: this.groupId,
                        accessId,
                    };
                    save('accesspoint/assign', params, () => {
                        this.changePage(1, true);
                    });
                }
            },

            assignRange() {
                if (
                    confirm(
                        this.$t(this.selected.id === this.$store.user.orgId ? 'ASSIGN_CONFIRM_1' : 'ASSIGN_CONFIRM_2'),
                    )
                ) {
                    const params = {
                        orgId: this.selected.id,
                        siteId: this.siteId,
                        userGroupId: this.groupId,
                        from: this.rangeFrom,
                        to: this.rangeTo,
                    };
                    save('accesspoint/assignrange', params, () => {
                        this.changePage(1, true);
                    });
                }
            },

            retrieveNumber(accessId) {
                if (confirm(this.$t('RETRIEVE_CONFIRM'))) {
                    save('accesspoint/retrieve', { accessId }, () => {
                        this.changePage(1, true);
                    });
                }
            },

            retrieveRange() {
                if (confirm(this.$t('RETRIEVE_CONFIRM'))) {
                    save('accesspoint/retrieverange', { from: this.rangeFrom, to: this.rangeTo }, () => {
                        this.changePage(1, true);
                    });
                }
            },

            changePage(val, reset = false) {
                this.page = val;
                this.loadNumbers(val, reset);
            },
            findMatch(branch) {
                if (branch.name.toUpperCase().substring(0, this.searchText.length) === this.searchText.toUpperCase()) {
                    return { id: branch.id, name: branch.name };
                } else if (!branch.leaf) {
                    const match = branch.children.find((x) => this.findMatch(x));
                    if (match) {
                        return this.findMatch(match);
                    }
                }

                return null;
            },
            handleClick(val) {
                if (this.selected.id !== val.id) {
                    this.selected = val;
                    this.changePage(1, true);
                }
            },
            handleSearch() {
                if (this.searchText !== this.lastSearch) {
                    if (this.searchText && this.searchText.split('').some((x) => new Blob([x]).size >= 3)) {
                        alert(this.$t('3 and 4 byte characters are not supported'));
                        return;
                    }

                    if (this.selected.id !== this.$store.user.orgId) {
                        this.selected = { id: this.$store.user.orgId, name: this.$store.user.orgName };
                        this.changePage(1, true);
                    }

                    if (this.searchText !== '') {
                        this.organisations = null;
                        load(
                            'organisation/tree',
                            {
                                orgId: this.$store.user.orgId,
                                searchText: this.searchText,
                                expandAll: 1,
                            },
                            (response) => {
                                if (response.organisations.length > 0) {
                                    this.organisations = [
                                        {
                                            name: this.$store.user.orgName,
                                            id: this.$store.user.orgId,
                                            leaf: false,
                                            children: response.organisations,
                                        },
                                    ];
                                    const match = this.findMatch(this.organisations[0]);
                                    if (match) {
                                        this.handleClick(match);
                                    }
                                }
                            },
                        );
                    }
                    this.lastSearch = this.searchText;
                }
            },
            loadNumbers(page = 1, reset = false) {
                this.numbers = [];
                this.sites = [];
                this.groups = [];
                if (reset) {
                    this.rangeFrom = null;
                    this.rangeTo = null;
                    this.siteId = null;
                    this.groupId = null;
                }

                load('accesspoints/assignable', { page, orgId: this.selected.id }, (response) => {
                    this.numbers = response.numbers;
                    this.totalRows = response.total;
                    this.sites = response.sites;
                    this.groups = response.userGroups;
                    if (!this.tableOrganisations.some((org) => org.orgId === response.organisation.orgId)) {
                        this.tableOrganisations.push(response.organisation);
                    }
                });
            },
        },
    };
</script>
